import {
  CLINICAL_OUTCOMES_MINIMAL_THRESHOLD,
  coerceBhConnectionCount,
} from '@act/shared/data-transfer-objects';
import {
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import SectionReveal from './SectionReveal';
import SectionBanner from './SectionBanner';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { body, CORAL, display, foreground, muted, NAVY } from './tokens';

const cardStyle = {
  background: '#fff',
  border: '0.5px solid var(--reema-midnight-25)',
  borderRadius: 24,
  padding: '2.5rem',
} as const;

const readNumberArray = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.map(item => coerceBhConnectionCount(item))
    : [];

const readMonthLabels = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(item => String(item)) : [];

interface OutcomesTrendChartProps {
  title: string;
  data: Array<{ label: string; value: number }>;
  stroke: string;
  minimalThreshold: number;
}

/**
 * Renders a PHQ-9 or GAD-7 line chart with point labels and a minimal-threshold reference line.
 * @param props - Chart title, series data, stroke color, and threshold value.
 */
const OutcomesTrendChart = ({
  title,
  data,
  stroke,
  minimalThreshold,
}: OutcomesTrendChartProps) => (
  <div style={cardStyle}>
    <h3
      style={{
        fontFamily: display,
        fontWeight: 600,
        fontSize: 18,
        color: foreground,
        margin: '0 0 1rem',
        lineHeight: 1.2,
      }}
    >
      {title}
    </h3>
    <div style={{ width: '100%', height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 24, right: 56, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E8E0D6" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: muted }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: muted }}
            width={40}
            axisLine={false}
            tickLine={false}
            domain={[0, 'auto']}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 12,
              border: '1px solid #E8E0D6',
              fontSize: 13,
            }}
            formatter={(value: number) => [value, 'Score']}
          />
          <ReferenceLine
            y={minimalThreshold}
            stroke={NAVY}
            strokeDasharray="4 4"
            strokeWidth={1}
            opacity={0.6}
            label={{
              value: 'Minimal',
              position: 'right',
              fill: muted,
              fontSize: 11,
            }}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={stroke}
            strokeWidth={2.5}
            dot={{ r: 5, fill: stroke, strokeWidth: 0 }}
            activeDot={{ r: 6 }}
          >
            <LabelList
              dataKey="value"
              position="top"
              offset={12}
              style={{ fill: foreground, fontSize: 12, fontWeight: 500 }}
            />
          </Line>
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

/**
 * Clinical Care Outcomes: stat cards and PHQ-9 / GAD-7 trend charts.
 * @param props - Resolved section snapshot.
 */
const ClinicalCareOutcomes = ({
  data,
}: ReportHostedSectionProps) => {
  const membersInClinicalCare = coerceBhConnectionCount(data.membersInClinicalCare);
  const activeThisMonth = coerceBhConnectionCount(data.activeThisMonth);
  const treatmentGoalsIdentified = coerceBhConnectionCount(
    data.treatmentGoalsIdentified,
  );
  const symptomReductionPct3Mo = coerceBhConnectionCount(
    data.symptomReductionPct3Mo,
  );
  const minimalThreshold =
    coerceBhConnectionCount(data.minimalThreshold) ||
    CLINICAL_OUTCOMES_MINIMAL_THRESHOLD;
  const months = readMonthLabels(data.months);
  const phq9Series = readNumberArray(data.phq9Series);
  const gad7Series = readNumberArray(data.gad7Series);

  const phqChartData = months.map((label, index) => ({
    label,
    value: phq9Series[index] ?? 0,
  }));
  const gadChartData = months.map((label, index) => ({
    label,
    value: gad7Series[index] ?? 0,
  }));

  return (
    <HostedReportSectionLayout
      sectionDomId="clinical-care-outcomes"
      className="section--white report-hosted-section-body"
      style={{ fontFamily: body }}
      banner={
        <SectionBanner
          title="Clinical Care Outcomes"
          iconHref="#icon_behavioral_health"
        />
      }
    >
      <SectionReveal>
        <ReportMockFieldFrame data={data} field="membersInClinicalCare">
          <div className="stat-grid stat-grid--4" style={{ marginBottom: '4rem' }}>
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">
                {membersInClinicalCare.toLocaleString()}
              </div>
              <div className="stat-label">Members in clinical care</div>
            </div>
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">
                {activeThisMonth.toLocaleString()}
              </div>
              <div className="stat-label">Active this month</div>
            </div>
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">
                {treatmentGoalsIdentified.toLocaleString()}
              </div>
              <div className="stat-label">Treatment Goals Identified</div>
            </div>
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">{symptomReductionPct3Mo}%</div>
              <div className="stat-label">
                Reporting symptom reduction at 3 months
              </div>
            </div>
          </div>
        </ReportMockFieldFrame>

        <div
          className="two-col two-col--equal"
          style={{ marginBottom: '4rem', gap: '1.5rem' }}
        >
          <ReportMockFieldFrame data={data} field="phq9Series">
            <OutcomesTrendChart
              title="PHQ-9 depression scores"
              data={phqChartData}
              stroke={CORAL}
              minimalThreshold={minimalThreshold}
            />
          </ReportMockFieldFrame>
          <ReportMockFieldFrame data={data} field="gad7Series">
            <OutcomesTrendChart
              title="GAD-7 anxiety scores"
              data={gadChartData}
              stroke={NAVY}
              minimalThreshold={minimalThreshold}
            />
          </ReportMockFieldFrame>
        </div>
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default ClinicalCareOutcomes;
